// src/components/admin/AdminDashboard.jsx
import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import AppShell from "../layout/AppShell";
import TripCard from "../trips/TripCard";
import { Card, StatCard, Badge, Button, Stars, Avatar, EmptyState, Spinner, Input, T } from "../ui";
import { useAllTrips } from "../../hooks/useTrips";
import { useCaptains, useClients, usePendingCaptains } from "../../hooks/useUsers";
import { approveCaptain, rejectCaptain, updateSettings, toggleCaptainDisabled } from "../../services/userService";
import { useSettings } from "../../contexts/SettingsContext";
import { AdminSupportPanel } from "../support/SupportChat";
import { subscribeToAllChats, subscribeUnreadCount } from "../../services/supportService";

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const { data: trips, loading: tLoading } = useAllTrips();
  const { data: allCaptains } = useCaptains();
  const { data: clients } = useClients();
  const { data: pendingCaptains } = usePendingCaptains();
  const settings = useSettings();
  const [sett, setSett] = useState(null);

  const [chats, setChats] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const u1 = subscribeToAllChats(setChats, console.error);
    const u2 = subscribeUnreadCount(setUnreadCount);
    return () => { u1(); u2(); };
  }, []);

  const approvedCaptains = allCaptains.filter(c => c.status === "approved");
  const captainProfiles = useMemo(() => Object.fromEntries(allCaptains.map(c => [c.id, c])), [allCaptains]);

  const stats = useMemo(() => ({
    total: trips.length,
    pending: trips.filter(t => t.status === "pending").length,
    review: trips.filter(t => t.status === "deposit_review").length,
    confirmed: trips.filter(t => t.status === "confirmed").length,
    completed: trips.filter(t => t.status === "completed").length,
  }), [trips]);

  const tabs = [
    { key: "overview", label: "نظرة عامة", icon: "📊" },
    { key: "trips", label: "الرحلات", icon: "🗺️" },
    { key: "approvals", label: "الموافقات", icon: "✅", badge: pendingCaptains.length },
    { key: "captains", label: "الكباتن", icon: "🚘" },
    { key: "clients", label: "العملاء", icon: "👤" },
    { key: "support", label: "الدعم", icon: "💬", badge: unreadCount },
    { key: "settings", label: "الإعدادات", icon: "⚙️" },
  ];

  const cur = sett || settings;
  const setS = (k, v) => setSett(p => ({ ...(p || settings), [k]: v }));

  const handleApprove = async id => { try { await approveCaptain(id); toast.success("✓ تم قبول الطلب"); } catch (e) { toast.error(e.message); } };
  const handleReject = async id => { try { await rejectCaptain(id); toast.success("تم رفض الطلب"); } catch (e) { toast.error(e.message); } };
  const handleSave = async () => { try { await updateSettings(cur); toast.success("💾 تم حفظ الإعدادات"); } catch (e) { toast.error(e.message); } };

  return (
    <AppShell tabs={tabs} activeTab={tab} onTabChange={setTab}>

      {/* ── OVERVIEW ── */}
      {tab === "overview" && (
        <>
          {pendingCaptains.length > 0 && (
            <div onClick={() => setTab("approvals")} style={{ background: "#FEF3C7", borderRadius: 14, padding: "12px 18px", marginBottom: 14, border: "1px solid #FDE68A", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span><b>⚠️ {pendingCaptains.length} كابتن</b> في انتظار الموافقة</span>
              <span style={{ color: T.accent, fontWeight: 700 }}>مراجعة ←</span>
            </div>
          )}
          {stats.review > 0 && (
            <div onClick={() => setTab("trips")} style={{ background: "#EDE9FE", borderRadius: 14, padding: "12px 18px", marginBottom: 14, border: "1px solid #DDD6FE", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span><b>🔍 {stats.review} تحويل</b> في انتظار المراجعة</span>
              <span style={{ color: T.purple, fontWeight: 700 }}>مراجعة ←</span>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 12 }}>
            <StatCard label="إجمالي" value={stats.total} icon="🗺️" color={T.info} />
            <StatCard label="قيد البحث" value={stats.pending} icon="⏳" color={T.gold} />
            <StatCard label="مراجعة" value={stats.review} icon="🔍" color={T.purple} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 24 }}>
            <StatCard label="مؤكدة" value={stats.confirmed} icon="✅" color={T.success} />
            <StatCard label="مكتملة" value={stats.completed} icon="🏁" color={T.teal} />
            <StatCard label="الكباتن" value={approvedCaptains.length} icon="🚘" color={T.accent} />
          </div>
          <h3 style={{ margin: "0 0 14px", fontWeight: 800 }}>أحدث الرحلات</h3>
          {tLoading ? <Spinner /> : trips.slice(0, 4).map(t => <TripCard key={t.id} trip={t} captainProfiles={captainProfiles} />)}
        </>
      )}

      {/* ── TRIPS ── */}
      {tab === "trips" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>إدارة الرحلات <span style={{ fontSize: 14, color: T.muted, fontWeight: 400 }}>({trips.length})</span></h2>
          {tLoading ? <Spinner /> : trips.length === 0 ? <EmptyState icon="🗺️" title="لا توجد رحلات" /> : trips.map(t => <TripCard key={t.id} trip={t} captainProfiles={captainProfiles} />)}
        </>
      )}

      {/* ── APPROVALS ── */}
      {tab === "approvals" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>طلبات تسجيل الكباتن</h2>
          {pendingCaptains.length === 0
            ? <EmptyState icon="✅" title="لا توجد طلبات معلقة" />
            : pendingCaptains.map(cap => <ApprovalCard key={cap.id} cap={cap} onApprove={handleApprove} onReject={handleReject} />)}
        </>
      )}

      {/* ── CAPTAINS ── */}
      {tab === "captains" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>الكباتن <span style={{ fontSize: 14, color: T.muted, fontWeight: 400 }}>({approvedCaptains.length})</span></h2>
          {approvedCaptains.length === 0 ? <EmptyState icon="🚘" title="لا يوجد كباتن معتمدون" /> : approvedCaptains.map(cap => <CaptainCard key={cap.id} cap={cap} />)}
        </>
      )}

      {/* ── CLIENTS ── */}
      {tab === "clients" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>العملاء <span style={{ fontSize: 14, color: T.muted, fontWeight: 400 }}>({clients.length})</span></h2>
          {clients.length === 0 ? <EmptyState icon="👤" title="لا يوجد عملاء" /> : clients.map(cl => (
            <Card key={cl.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Avatar name={cl.name} size={44} color={T.success} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{cl.name}</div>
                <div style={{ fontSize: 12, color: T.muted }}>{cl.email} • {cl.phone}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.accent }}>{trips.filter(t => t.clientId === cl.id).length}</div>
                <div style={{ fontSize: 11, color: T.muted }}>رحلة</div>
              </div>
            </Card>
          ))}
        </>
      )}

      {/* ── SUPPORT ── */}
      {tab === "support" && (
        <>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>💬 رسائل الدعم</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: T.muted }}>
              {chats.length} محادثة
              {unreadCount > 0 && <span style={{ color: T.danger, fontWeight: 700, marginRight: 8 }}>• {unreadCount} غير مقروءة</span>}
            </p>
          </div>
          <AdminSupportPanel chats={chats} />
        </>
      )}

      {/* ── SETTINGS ── */}
      {tab === "settings" && (
        <>
          <h2 style={{ margin: "0 0 20px", fontSize: 20, fontWeight: 800 }}>إعدادات المنصة</h2>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>💰 الديبوزت (بالجنيه)</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <Input label="الحد الأدنى (ج.م)" type="number" value={cur.depositMin || 50} onChange={e => setS("depositMin", +e.target.value)} />
              <Input label="الحد الأقصى (ج.م)" type="number" value={cur.depositMax || 500} onChange={e => setS("depositMax", +e.target.value)} />
              <Input label="قيمة الديبوزت (ج.م)" type="number" value={cur.depositFixed || 100} onChange={e => setS("depositFixed", +e.target.value)} />
            </div>
            <div style={{ background: "#EFF6FF", borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              📌 الديبوزت المطلوب: <b>{cur.depositFixed || 100} ج.م</b> | النطاق: {cur.depositMin || 50}–{cur.depositMax || 500} ج.م
            </div>
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>⏱ حدود الوقت</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Input label="مهلة البحث (دقيقة)" type="number" value={cur.searchLimitMinutes || 60} onChange={e => setS("searchLimitMinutes", +e.target.value)} />
              <Input label="مهلة الديبوزت (دقيقة)" type="number" value={cur.depositLimitMinutes || 30} onChange={e => setS("depositLimitMinutes", +e.target.value)} />
              <Input label="مهلة المراجعة (ساعة)" type="number" value={cur.depositReviewLimitHours || 24} onChange={e => setS("depositReviewLimitHours", +e.target.value)} />
            </div>
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>📱 InstaPay والدعم</h3>
            <Input label="رقم InstaPay" value={cur.instaPayNumber || ""} onChange={e => setS("instaPayNumber", e.target.value)} />
            <Input label="اسم الحساب" value={cur.instaPayName || ""} onChange={e => setS("instaPayName", e.target.value)} />
            <Input
              label="رابط الدفع المخصص (اختياري)"
              value={cur.instaPayLink || ""}
              onChange={e => setS("instaPayLink", e.target.value)}
              placeholder="https://ipn.eg/S/..."
            />
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 14, marginTop: -8 }}>
              لو فارغ سيُستخدم رابط InstaPay القياسي من الرقم أعلاه
            </div>
            <Input label="رقم تليفون الدعم الفني" value={cur.supportPhone || ""} onChange={e => setS("supportPhone", e.target.value)} placeholder="01X..." />
          </Card>

          <Card>
            <h3 style={{ margin: "0 0 14px", fontWeight: 800 }}>⚙️ عمولة المنصة</h3>
            <Input label="نسبة العمولة (%)" type="number" value={cur.commission || 15} onChange={e => setS("commission", +e.target.value)} />
          </Card>

          <Button variant="purple" full size="lg" onClick={handleSave}>💾 حفظ جميع الإعدادات</Button>
        </>
      )}
    </AppShell>
  );
}

// ── APPROVAL CARD ─────────────────────────────────────────────
function ApprovalCard({ cap, onApprove, onReject }) {
  const [busy, setBusy] = useState(false);
  const [carOpen, setCarOpen] = useState(false);

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {cap.photoUrl
          ? <img src={cap.photoUrl} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          : <Avatar name={cap.name} size={52} color={T.gold} />
        }
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{cap.name}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{cap.email}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{cap.phone}</div>
          <div style={{ fontSize: 13, marginTop: 3, fontWeight: 600 }}>{cap.car} • {cap.plate}</div>
        </div>
        <Badge color="#92400E" bg="#FEF3C7">معلق</Badge>
      </div>

      {/* Document images */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 8 }}>📎 المستندات</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[["رخصة القيادة", cap.licenseUrl], ["بطاقة الهوية", cap.idUrl]].map(([label, url]) => (
            url
              ? <a key={label} href={url} target="_blank" rel="noreferrer" style={{ display: "block", borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}`, textDecoration: "none" }}>
                <img src={url} alt={label} style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }} />
                <div style={{ padding: "6px 8px", background: T.surfAlt, fontSize: 11, color: T.muted, fontWeight: 600, textAlign: "center" }}>{label} ↗</div>
              </a>
              : <div key={label} style={{ borderRadius: 10, border: "1px solid #FECACA", background: "#FEF2F2", height: 100, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontSize: 24 }}>❌</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{label} غير مرفوع</div>
              </div>
          ))}
        </div>
      </div>

      {/* Car photos */}
      {cap.carPhotoUrls?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setCarOpen(true)} style={{ width: "100%", padding: 10, borderRadius: 10, border: `1px solid ${T.border}`, background: T.surfAlt, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: T.teal }}>
            🚗 عرض صور السيارة ({cap.carPhotoUrls.length})
          </button>
          {carOpen && (
            <div onClick={() => setCarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 16, padding: 20, maxWidth: 480, width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                  <b>صور سيارة {cap.name}</b>
                  <button onClick={() => setCarOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18 }}>✕</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {cap.carPhotoUrls.map((url, i) => <a key={i} href={url} target="_blank" rel="noreferrer"><img src={url} alt="" style={{ width: "100%", height: 130, objectFit: "cover", borderRadius: 10, display: "block" }} /></a>)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="success" style={{ flex: 1 }} loading={busy} onClick={async () => { setBusy(true); await onApprove(cap.id); setBusy(false); }}>✓ قبول</Button>
        <Button variant="danger" loading={busy} onClick={async () => { setBusy(true); await onReject(cap.id); setBusy(false); }}>✕ رفض</Button>
      </div>
    </Card>
  );
}

// ── CAPTAIN CARD ──────────────────────────────────────────────
function CaptainCard({ cap }) {
  const [busy, setBusy] = useState(false);

  const handleToggle = async () => {
    setBusy(true);
    try { await toggleCaptainDisabled(cap.id, cap.disabled); toast.success(cap.disabled ? "✓ تم تفعيل الحساب" : "تم تعطيل الحساب"); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        {cap.photoUrl
          ? <img src={cap.photoUrl} alt="" style={{ width: 46, height: 46, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          : <Avatar name={cap.name} size={46} color={T.teal} />
        }
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{cap.name}</span>
            {cap.disabled && <Badge color="#991B1B" bg="#FEE2E2">معطل</Badge>}
          </div>
          <div style={{ fontSize: 12, color: T.muted }}>{cap.email} • {cap.phone}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{cap.car} • {cap.plate}</div>
          <Stars rating={cap.rating || 0} size={13} />
        </div>
        <Badge color={cap.available ? "#065F46" : "#991B1B"} bg={cap.available ? "#D1FAE5" : "#FEE2E2"}>
          {cap.available ? "متاح" : "غير متاح"}
        </Badge>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <StatCard label="رحلة" value={cap.trips || 0} icon="🏁" color={T.info} />
        <StatCard label="رصيد ج.م" value={cap.balance || 0} icon="💰" color={T.success} />
        <StatCard label="تقييم" value={Number(cap.rating || 0).toFixed(1)} icon="⭐" color={T.gold} />
      </div>
      <Button variant={cap.disabled ? "success" : "danger"} size="sm" full loading={busy} onClick={handleToggle}>
        {cap.disabled ? "✓ تفعيل الحساب" : "⛔ تعطيل الحساب"}
      </Button>
    </Card>
  );
}
